import { Link } from "react-router-dom";

import ArrowLeft from "../../components/LogoSVG/ArrowLeft";
import ArticleHeaderMobile from "../../components/Global/ArticleHeaderMobile";
import TechStackComponent from "../../components/Global/TechStackComponent";
import Header1Mobile from "../../components/Global/Header1Mobile";
import Header2 from "../../components/Global/Header2Desktop";
import Paragraph from "../../components/Global/Paragraph";

import reactNativeLogo from "../../public/react-native.png";
import expoLogo from "../../public/expo.png";
import googleMapLogo from "../../public/google-map.png";
import tsLogo from "../../public/ts-logo.png";
import thumbnail from "../../public/thumbnail-map2.jpg";
import mappicker from "../../public/pickMap1.png";
import mockupBlog from "../../public/mockup-blog3.png";
import videoMap1 from "../../public/video/map1.mp4";
import videoMap2 from "../../public/video/map2.mp4";

const BlogMapMobile = () => {
  return (
    <div className="mx-auto max-w-[730px] px-6 pb-24 pt-12">
      <Link to={"/"} className="flex items-center cursor-pointer">
        <ArrowLeft className="w-6 h-6" />
        <p className="ml-2 mt-1 font-normal text-gray-5 text-center hover:text-custom-white">
          Back
        </p>
      </Link>

      <ArticleHeaderMobile
        thumbnail={thumbnail}
        title="Picking a location with React Native using a performance-oriented approach like Grab, Uber, and others do"
        date="May 23, 2024"
        githubRepo="https://github.com/lnqminh3003/Picking-a-location-with-React-Native"
        linkedinPost="https://www.linkedin.com/feed/update/urn:li:activity:7199243827939876864/"
      />

      <div className="-mt-4">
        <div>
          <Header1Mobile>Overview of Location Selection</Header1Mobile>
          <Paragraph>
            In this section, I will briefly discuss the evolution of location
            selection methods from the past to the present. With the increasing
            use of location-based services—such as finding favorite places or
            booking transportation—it's important to understand how these
            methods have evolved over time.
          </Paragraph>

          <Header2>1. Traditional Methods</Header2>
          <Paragraph>
            Historically, users have had two primary ways to select a location:
          </Paragraph>
          <Paragraph>
            <span className="mr-0.5 text-custom-green">
              Dragging a Map Marker
            </span>
            : Users manually drag a marker across a map to select a location.
            While simple, this method often results in low precision and can be
            cumbersome.
          </Paragraph>
          <Paragraph>
            <span className="mr-0.5 text-custom-green">Search Engines</span>:
            Users can search for places using search engines. However, this
            method is limited by the completeness of databases like Google and
            Apple Places, which may not include all buildings.
          </Paragraph>

          <img
            alt=""
            fetchPriority="high"
            decoding="async"
            data-nimg="1"
            className="rounded-md w-[calc(100%)] max-w-none mb-8 mt-4"
            style={{ color: "transparent" }}
            src={mappicker}
          ></img>

          <Header2>2. Modern Approaches</Header2>
          <Paragraph>
            To address these issues, most tech companies have refined location
            selection. A common approach involves{" "}
            <span className="mx-0.5  text-custom-green">
              placing a marker at the center of the map, allowing users to pan
              around it
            </span>
            . This method, used by companies like Uber and Grab, offers a
            smoother and more intuitive experience, requiring minimal effort
            from users.
          </Paragraph>
        </div>

        <div>
          <Header1Mobile>Technology</Header1Mobile>
          <Paragraph>
            The technology we opted for the project revolved heavily around
            TypeScript. I chose to leverage React Native due to its capabilities
            for cross-platform mobile development. The Expo ecosystem enhances
            React Native and makes it a powerful tool for building mobile
            applications.
          </Paragraph>
          <Paragraph>
            For this project, I utilized the Geocoding API from the Google Maps
            Platform. This service enable users to locate the their location by
            providing latitude and longitude coordinates, as well as converting
            these coordinates into human-readable addresses.
          </Paragraph>

          <div className="flex flex-row flex-wrap justify-evenly mt-16 mx-2">
            <TechStackComponent name="TypeScript" logo={tsLogo} />
            <TechStackComponent name="React Native" logo={reactNativeLogo} />
          </div>

          <div className="flex flex-row flex-wrap justify-evenly mb-16 mt-8 mx-2">
            <TechStackComponent name="Expo" logo={expoLogo} />
            <TechStackComponent name="Geocoding API" logo={googleMapLogo} />
          </div>
        </div>

        <div>
          <Header1Mobile>
            Challenges with the Expo MapView in React Native
          </Header1Mobile>
          <Paragraph>
            How can you achieve this with React Native and React Native Maps?
          </Paragraph>
          <Paragraph>
            This is precisely what I am working on. Let's first examine some of
            the challenges associated with
            <a
              className="underline underline-offset-4 ml-0.5 text-custom-green"
              href="https://github.com/react-native-maps/react-native-maps"
              target="_blank"
            >
              MapView
            </a>
            , a core map library provided by Expo and supported by both Google
            and the Expo team.
          </Paragraph>
          <Paragraph>
            The core library MapView, supported by Google and Expo, has a couple
            of issues:
          </Paragraph>

          <Paragraph>
            <span className="mr-1 font-medium text-custom-green">
              - onRegionChange
            </span>
            event : Using this event can lead to frequent state updates and
            excessive re-renders per frame, resulting in performance issues and
            lag.
          </Paragraph>
          <Paragraph>
            <span className="mr-1 font-medium text-custom-green">
              - onRegionChangeComplete
            </span>
            event: Sure, it only triggers after the user has finished panning
            around the map, but that's not ideal either. it means the marker
            only updates once the user has finished, which can be less
            responsive.
          </Paragraph>
        </div>

        <div>
          <Header1Mobile>My Solution and Proposal</Header1Mobile>
          <Header2>1. The logic</Header2>
          <Paragraph>
            Use a fake marker (static) positioned at the center of the map. As
            the user moves the map around, my application captures the
            coordinates (longitude and latitude) and update the region
            information after they stop panning. After that, I use the Google
            Maps Geocoding API to convert these coordinates into a
            human-readable address.
          </Paragraph>
          <Paragraph>
            At the same time, I include a search box for faster location picking
            and to enhance the user experience.
          </Paragraph>

          <Header2>2. The interface</Header2>
          <Paragraph>
            From the start, the idea was to keep the interface as intuitive as
            possible to enhance the user experience for seamless location
            selection. Therefore, I intended to create an interface in mobile by
            using React Native that would show a map with a central marker and
            an autocomplete search bar to facilitate quick and accurate location
            selection.
          </Paragraph>

          <img
            alt=""
            fetchPriority="high"
            decoding="async"
            data-nimg="1"
            className="rounded-md w-[100%] max-w-none mb-8 mt-12"
            style={{ color: "transparent" }}
            src={mockupBlog}
          ></img>
        </div>

        <div>
          <Header1Mobile>The Final Result</Header1Mobile>
          <Paragraph>
            The proposed solution aims to improve user experience by combining a
            central marker with efficient location updating and an intuitive
            search feature.
          </Paragraph>

          <div className="flex justify-around items-center mt-12">
            <video
              className="rounded-lg w-[150px]"
              controls
              autoPlay
              loop
              muted
            >
              <source src={videoMap1} type="video/mp4" />
            </video>

            <video
              className="rounded-lg w-[150px]"
              controls
              autoPlay
              loop
              muted
            >
              <source src={videoMap2} type="video/mp4" />
            </video>
          </div>
        </div>

        <div>
          <Header1Mobile>Conclusion and Notes</Header1Mobile>
          <Paragraph>
            Adding a map to a React Native app can seem daunting at first, but
            with the right tools and libraries, it can be a fun and rewarding
            experience. Expo MapView is a powerful library that makes it easy to
            add beautiful, interactive maps to your app.
          </Paragraph>
          <Paragraph>
            This is super important for{" "}
            <span className="mx-0.5 text-custom-white">Android</span> users, you
            have to put the{" "}
            <span className="mx-0.5 text-custom-white">View</span> with the
            Image outside of the
            <span className="ml-1 text-custom-white">MapView.</span>
          </Paragraph>
          <Paragraph>
            Remember to set{" "}
            <span className="ml-0.5 text-custom-green font-medium">
              pointerEvents.
            </span>
            to none in the style for the fake marker. That'll make the marker
            unclickable and allow smooth map panning.
          </Paragraph>
          <Paragraph>
            If you have more markers on the map, use similar images for all.
            The 
            <span className="mr-1 ml-0.5 text-custom-green font-medium">
              Marker
            </span>
            component from React Native Maps is from Apple Maps or Google Maps,
            depending on your platform and setup, and will probably look
            different than your image.
          </Paragraph>
        </div>

        <div>
          <Header1Mobile>Useful links:</Header1Mobile>
          <Paragraph>
            <a
              className="underline underline-offset-4"
              href="https://github.com/FaridSafi/react-native-google-places-autocomplete"
              target="_blank"
            >
              React Native Google Places
            </a>
          </Paragraph>
          <Paragraph>
            <a
              className="underline underline-offset-4"
              href="https://developers.google.com/maps/documentation/geocoding/requests-reverse-geocoding"
              target="_blank"
            >
              Readable address API (Geocoding)
            </a>
          </Paragraph>
          <Paragraph>
            <a
              className="underline underline-offset-4"
              href="https://github.com/react-native-maps/react-native-maps"
              target="_blank"
            >
              React Native Mapview
            </a>
          </Paragraph>
        </div>
      </div>
    </div>
  );
};

export default BlogMapMobile;
